// KinPOS/PAX solo puede anular una venta desde el mismo dispositivo Android (AndroidBridge) que la
// cobro (ver docs/kinpos-jsbridge-contract.md). Cuando la devolucion se inicia desde un punto (B)
// distinto al que cobro (A), este modulo enruta la orden de anulacion por WebSocket a traves del
// backend (relay sin estado, ver smartpay-dashboard/src/app/sockets/handlers/kinposRemoteVoidHandler.js)
// para que el punto A ejecute el void con su propio KinposPaymentBridge y devuelva el resultado.
//
// Protocolo (4 mensajes, ids de correlacion propios, no confundir con el request_id interno de
// KinposPaymentBridge en el punto A):
//   1. B -> requestRemoteKinposVoid  -> A recibe remoteKinposVoidRequested
//   2. A -> respondRemoteKinposVoid  -> B recibe remoteKinposVoidResult
//   3. B -> confirmRemoteKinposVoid  -> A recibe remoteKinposVoidConfirmed
//
// Este modulo no posee el socket: cada llamador (ModalReturn en B, el executor en A) es dueno de
// su propia conexion de Socket.config, igual que el resto del POS.
import { generateToken } from "./GenerateToken";

export const REMOTE_VOID_TIMEOUT_MS = 45000;

const createUniqueId = (prefix) => `${prefix}-${generateToken()}`;

export const createRemoteVoidRequestId = () => createUniqueId("remote-void");

// A: marca el context de un void ejecutado por encargo de otro punto, para que
// KinposVoidResultRouter lo enrute al executor en vez de al ModalReturn local de este mismo
// dispositivo. Se conserva dentro del context persistido por KinposPaymentBridge, asi que
// sobrevive a un reload de pagina en A (recover() sigue enrutando correctamente).
export const tagContextForRemoteRelay = (context, { remoteRequestId, requestingMachineCode, businessCode, branchofficeCode }) => ({
   ...context,
   remote_relay: {
      remote_request_id: remoteRequestId,
      requesting_machine_code: requestingMachineCode,
      business_code: businessCode,
      branchoffice_code: branchofficeCode,
   },
});

// B: arma y envia la solicitud de anulacion remota, y espera la respuesta correlacionada por
// remote_request_id. Rechaza si no llega respuesta dentro de timeoutMs (punto A apagado/desconectado)
// o si la respuesta llega marcada como error del relay (payload incompleto).
export const requestRemoteKinposVoid = (socket, {
   targetMachineCode,
   businessCode,
   branchofficeCode,
   requestingMachineCode,
   orderReference,
   receiptNumber,
   payment,
   context,
}, { timeoutMs = REMOTE_VOID_TIMEOUT_MS } = {}) => {
   const remote_request_id = createRemoteVoidRequestId();

   return new Promise((resolve, reject) => {
      let settled = false;

      const cleanup = () => {
         clearTimeout(timeoutHandle);
         socket.off("remoteKinposVoidResult", handleResult);
      };

      const handleResult = (data) => {
         const payload = typeof data === "string" ? JSON.parse(data) : data;
         if (!payload || payload.remote_request_id !== remote_request_id || settled) return;
         settled = true;
         cleanup();
         if (payload.error) {
            reject(new Error(payload.message || "No se pudo enrutar la anulacion remota de KinPOS."));
            return;
         }
         resolve({ remote_request_id, kinpos_request_id: payload.kinpos_request_id, result: payload.result });
      };

      const timeoutHandle = setTimeout(() => {
         if (settled) return;
         settled = true;
         cleanup();
         reject(new Error(
            `No se pudo contactar el punto original (${targetMachineCode}). Verifique que este encendido y conectado.`
         ));
      }, timeoutMs);

      socket.on("remoteKinposVoidResult", handleResult);
      socket.emit("requestRemoteKinposVoid", {
         remote_request_id,
         target_machine_code: targetMachineCode,
         business_code: businessCode,
         branchoffice_code: branchofficeCode,
         requesting_machine_code: requestingMachineCode,
         void: { orderReference, receiptNumber },
         payment,
         context,
      });
   });
};

// B: confirma a A si puede hacer ACK a Android (success=true) o debe conservar el pending como
// recuperable (success=false), replicando exactamente lo que handleKinposVoidResult hace localmente
// con kinposPaymentBridge.complete()/markProcessingFailed() cuando el void es local.
export const confirmRemoteKinposVoid = (socket, {
   remoteRequestId,
   kinposRequestId,
   targetMachineCode,
   businessCode,
   branchofficeCode,
   success,
}) => {
   socket.emit("confirmRemoteKinposVoid", {
      remote_request_id: remoteRequestId,
      kinpos_request_id: kinposRequestId,
      target_machine_code: targetMachineCode,
      business_code: businessCode,
      branchoffice_code: branchofficeCode,
      success,
   });
};

// A: se suscribe a las solicitudes de anulacion remota dirigidas a este punto. Devuelve una funcion
// para cancelar la suscripcion.
export const listenForRemoteKinposVoidRequests = (socket, handler) => {
   const wrappedHandler = (data) => handler(typeof data === "string" ? JSON.parse(data) : data);
   socket.on("remoteKinposVoidRequested", wrappedHandler);
   return () => socket.off("remoteKinposVoidRequested", wrappedHandler);
};

// A: responde con el resultado normalizado que produjo su propio KinposPaymentBridge, una vez que
// Android devolvio un estado terminal (approved/declined/cancelled/error).
export const respondRemoteKinposVoid = (socket, {
   remoteRequestId,
   kinposRequestId,
   requestingMachineCode,
   businessCode,
   branchofficeCode,
   result,
}) => {
   socket.emit("respondRemoteKinposVoid", {
      remote_request_id: remoteRequestId,
      kinpos_request_id: kinposRequestId,
      requesting_machine_code: requestingMachineCode,
      business_code: businessCode,
      branchoffice_code: branchofficeCode,
      result,
   });
};

// A: se suscribe a la confirmacion de B (ACK o fallo de registro). Devuelve una funcion para
// cancelar la suscripcion.
export const listenForRemoteKinposVoidConfirmation = (socket, handler) => {
   const wrappedHandler = (data) => handler(typeof data === "string" ? JSON.parse(data) : data);
   socket.on("remoteKinposVoidConfirmed", wrappedHandler);
   return () => socket.off("remoteKinposVoidConfirmed", wrappedHandler);
};
